// Utilidades compartidas para convocatorias: próximo partido, rotación de
// jugadores convocados recientemente y mensaje para compartir por WhatsApp.

export function esPartidoPendiente(partido) {
  const resultado = (partido?.resultado || "").trim();
  return !resultado || resultado === "-";
}

export function tieneConvocatoria(partido) {
  return (partido?.asistencia?.jugadores?.length || 0) > 0;
}

// Días de diferencia entre hoy y la fecha dada (0 = hoy, negativo = pasado).
export function diasHasta(fechaStr) {
  if (!fechaStr) return null;
  const fecha = new Date(fechaStr);
  if (Number.isNaN(fecha.getTime())) return null;
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  fecha.setHours(0, 0, 0, 0);
  return Math.round((fecha - hoy) / 86400000);
}

// Primer partido pendiente (sin resultado) con fecha hoy o futura.
export function getProximoPartidoPendiente(partidos = []) {
  const candidatos = partidos
    .filter((p) => p.fecha && esPartidoPendiente(p))
    .map((p) => ({ partido: p, dias: diasHasta(p.fecha) }))
    .filter((c) => c.dias !== null && c.dias >= 0)
    .sort((a, b) => a.dias - b.dias);
  return candidatos[0] || null;
}

// Cuántas veces ha sido convocado un jugador en los últimos N partidos ya
// jugados de su equipo (para fomentar la rotación al armar la convocatoria).
export function contarConvocatoriasRecientes(
  jugadorId,
  partidos,
  idEquipo,
  { limite = 5, excluirPartidoId } = {}
) {
  const jugados = partidos
    .filter((p) => p.id_equipo === idEquipo && p._id !== excluirPartidoId && p.fecha)
    .filter((p) => {
      const dias = diasHasta(p.fecha);
      return dias !== null && dias < 0;
    })
    .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
    .slice(0, limite);

  const veces = jugados.filter((p) => (p.asistencia?.jugadores || []).includes(jugadorId)).length;
  return { veces, sobre: jugados.length };
}

// Igual que contarConvocatoriasRecientes pero para entrenamientos: cuántos de
// los últimos N entrenamientos ya realizados ha asistido el jugador.
export function contarAsistenciasEntrenamientoRecientes(
  jugadorId,
  entrenamientos,
  idEquipo,
  { limite = 5 } = {}
) {
  const realizados = entrenamientos
    .filter((e) => e.id_equipo === idEquipo && e.fecha)
    .filter((e) => {
      const dias = diasHasta(e.fecha);
      return dias !== null && dias < 0;
    })
    .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
    .slice(0, limite);

  const veces = realizados.filter((e) =>
    (e.asistencia?.jugadores || []).includes(jugadorId)
  ).length;
  return { veces, sobre: realizados.length };
}

export function saludoPorHora(date = new Date()) {
  return date.getHours() < 14 ? "Buenos días" : "Buenas tardes";
}

// Resta antelacionMin minutos a una hora "HH:MM". Devuelve null si no puede calcularse.
export function restarMinutos(horaStr, minutos) {
  if (!horaStr || !minutos) return null;
  const [h, m] = horaStr.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const total = (((h * 60 + m - minutos) % 1440) + 1440) % 1440;
  const hh = String(Math.floor(total / 60)).padStart(2, "0");
  const mm = String(total % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}

// Construye el texto listo para pegar/compartir en el grupo de WhatsApp del
// equipo. Usa el formato de WhatsApp (*negrita*, _cursiva_) para que el
// mensaje llegue ordenado en bloques: partido, horario y convocados.
export function construirMensajeConvocatoria({
  partido,
  equipoNombre,
  convocados = [],
  antelacionMin,
}) {
  const fechaObj = partido?.fecha ? new Date(partido.fecha) : null;
  const fechaLarga =
    fechaObj && !Number.isNaN(fechaObj.getTime())
      ? fechaObj.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })
      : null;
  const fechaTexto = fechaLarga
    ? fechaLarga.charAt(0).toUpperCase() + fechaLarga.slice(1)
    : "Fecha por confirmar";

  const horaPartido = partido?.hora || null;
  const horaConvocatoria = restarMinutos(horaPartido, antelacionMin);

  const jugadoresOrdenados = [...convocados].sort((a, b) => {
    if (a.numero && b.numero) return a.numero - b.numero;
    if (a.numero) return -1;
    if (b.numero) return 1;
    return (a.nombre || "").localeCompare(b.nombre || "");
  });

  const lineasJugadores = jugadoresOrdenados
    .map((j) => (j.numero ? `*${j.numero}*  ${j.nombre}` : `•  ${j.nombre}`))
    .join("\n");

  const rival = partido?.equipo_rival || "Rival por confirmar";
  const jornada = partido?.jornada ? `  _Jornada ${partido.jornada}_` : "";

  let lineaHora = "⏰ Hora por confirmar";
  if (horaPartido && horaConvocatoria) {
    lineaHora = `⏰ Citados a las *${horaConvocatoria}*, partido a las *${horaPartido}*`;
  } else if (horaPartido) {
    lineaHora = `⏰ Partido a las *${horaPartido}*`;
  }

  const condicion = partido?.local ? "🏠 Jugamos en casa" : "🚌 Jugamos fuera";
  const lineaLugar = partido?.lugar ? `${condicion}\n📍 ${partido.lugar}` : condicion;

  return [
    `⚽ *CONVOCATORIA ${(equipoNombre || "").toUpperCase()}*`,
    "",
    `🆚 *${rival}*${jornada}`,
    `📅 ${fechaTexto}`,
    lineaHora,
    lineaLugar,
    "",
    `👥 *Convocados (${jugadoresOrdenados.length})*`,
    lineasJugadores || "Por confirmar",
    "",
    "¡Vamos, equipo! 💪",
  ].join("\n");
}
